import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const QUERY_STRING_STORAGE_KEY = '_lc_qs';

/**
 * Component that tracks and persists query string parameters across page loads.
 * Merges new query parameters with existing ones in session storage.
 */
export default function QueryStringTracker(): React.ReactNode {
  const location = useLocation();

  useEffect(() => {
    // Get current query string (remove leading '?' if present)
    const currentParams = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;

    // Only proceed if there are query parameters
    if (!currentParams) {
      return;
    }

    try {
      // Get existing query string from session storage
      const existingQueryString = sessionStorage.getItem(
        QUERY_STRING_STORAGE_KEY
      );

      // Parse existing parameters
      const existingParams = new URLSearchParams(existingQueryString || '');

      // Parse current parameters
      const newParams = new URLSearchParams(currentParams);

      // Merge parameters (new params override existing ones with same key)
      newParams.forEach((value, key) => {
        existingParams.set(key, value);
      });

      // Store the merged query string back in session storage
      const mergedQueryString = existingParams.toString();
      if (mergedQueryString) {
        sessionStorage.setItem(QUERY_STRING_STORAGE_KEY, mergedQueryString);
      }
    } catch (error) {
      console.error('Error tracking query string:', error);
    }
  }, [location.search]);

  return null; // This component doesn't render anything
}

/**
 * Utility function to retrieve the stored query string from session storage
 */
export function getStoredQueryString(): string {
  try {
    return sessionStorage.getItem(QUERY_STRING_STORAGE_KEY) || '';
  } catch (error) {
    console.error('Error retrieving stored query string:', error);
    return '';
  }
}

export function getStoredQueryStringAsObject(): Record<string, string> {
  const queryString = getStoredQueryString();
  return queryString
    ? Object.fromEntries(new URLSearchParams(queryString).entries())
    : {};
}

/**
 * Utility function to clear the stored query string
 */
export function clearStoredQueryString(): void {
  try {
    sessionStorage.removeItem(QUERY_STRING_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing stored query string:', error);
  }
}
